import { useSuspenseQuery } from '@apollo/client';
import { Suspense, useMemo } from 'react';

import { ErrorBoundary } from '@xing-com/crate-core-error-boundary';
import {
  ModuleHeader,
  WarningMessageBox,
} from '@xing-com/crate-entity-pages-common';
import { getTrackingUrl } from '@xing-com/crate-entity-pages-common/src/tracking';
import { useEditContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-edit-context';
import { usePageContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-entity-page-context';
import {
  JobsCardsCarousel,
  JobsEmptyState,
  JobsSkeleton,
  JobsModuleCompanyJobsDocument,
  createJobsCards,
} from '@xing-com/crate-entity-pages-jobs';

const ErrorFallback: React.FC<{ onReloadClick?: () => void }> = ({
  onReloadClick = () => window.location.reload(),
}) => {
  return (
    <div data-testid="errorContainer">
      <WarningMessageBox
        headerText="EP_ERROR_HEADER"
        bodyText="EP_ERROR_BODY"
        buttonText="EP_ERROR_RELOAD_CTA"
        onClick={() => onReloadClick()}
      />
    </div>
  );
};

const SectionCompanyJobsInner: React.FC = () => {
  const { pageContext } = usePageContext();
  const companyId = pageContext.companyId;
  const pageSlug = pageContext.pageSlug;
  const { isEditing } = useEditContext();

  const { data, error, refetch } = useSuspenseQuery(
    JobsModuleCompanyJobsDocument,
    {
      variables: {
        jobsLimit: 12,
        returnAggregations: false,
        jobsQuery: {
          filter: {
            company: { id: [companyId!] },
            publishToCompany: null,
          },
        },
      },
      errorPolicy: 'ignore',
      notifyOnNetworkStatusChange: true,
    }
  );

  const totalJobs = data?.jobSearchByQuery?.collection.length ?? 0;

  const pageJobsLink = `${pageContext.basePath}/${pageSlug}/jobs`;

  const handleShowMoreClick = () => {
    const trackingLink = getTrackingUrl({
      to: pageJobsLink,
      trackingKey: 'trackingKey',
    });
    window.open(trackingLink);
  };

  const jobsCards = useMemo(() => {
    const similarJobs = data?.jobSearchByQuery?.collection ?? [];

    // @ts-expect-error query generated as null
    return createJobsCards(similarJobs);
  }, [data]);

  return (
    <>
      <ModuleHeader
        headlineCopyKey="EP_JOBS_HEADLINE"
        showMore={{
          ...(totalJobs > 0
            ? {
                'data-testid': 'JOBS_SHOWMORE_LINK',
                to: pageJobsLink,
                onClick: (ev) => {
                  ev.preventDefault();
                  handleShowMoreClick();
                },
                copyKey: 'EP_JOBS_LINK',
              }
            : {}),
        }}
        externalEditInfo={
          isEditing
            ? {
                copyKey: 'EP_JOBS_EDIT_HINT',
                cyTestId: 'EDIT_HINT_JOBS',
              }
            : null
        }
      />
      {totalJobs > 0 ? (
        <div data-testid="JOBS_MODULE">
          <JobsCardsCarousel
            cards={jobsCards}
            onCardBookmarkUpdate={() => refetch()}
          />
        </div>
      ) : null}
      {error ? <ErrorFallback onReloadClick={() => refetch()} /> : null}
      {totalJobs === 0 ? (
        <div data-testid="JOBS_MODULE">
          <JobsEmptyState />
        </div>
      ) : null}
    </>
  );
};

const Fallback: React.FC = () => {
  return (
    <>
      <ModuleHeader
        headlineCopyKey="EP_JOBS_HEADLINE"
        showMore={{ isLoading: true }}
      />
      <JobsSkeleton />
    </>
  );
};

export const SectionCompanyJobs: React.FC = () => {
  return (
    <ErrorBoundary onError={() => <ErrorFallback />}>
      <Suspense fallback={<Fallback />}>
        <SectionCompanyJobsInner />
      </Suspense>
    </ErrorBoundary>
  );
};
